// import { StrictMode } from "react";
import { createRoot } from 'react-dom/client';
import './index.css';
import './core/i18n';
import './core/meshBvh';
import Providers from './providers/index.tsx';

import * as Sentry from '@sentry/react';
import { registerSW } from 'virtual:pwa-register';
import { resolveCustomDomain } from './core/customDomain';
import { isCustomDomain } from './core/domain';
import { isTauri } from './lib/offline/isTauri';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: !import.meta.env.DEV,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  environment: import.meta.env.VITE_STAGE as string,
});

// Async IIFE: on a custom domain we must resolve the hostname→org mapping
// (and cache it) before the Router renders, since the whole route tree is
// picked off getOrgSlugFromHostname() which reads that cache. Everything
// that used to run synchronously after createRoot(...).render(...) now runs
// after this gate so it still fires exactly once, in the same order.
void (async () => {
  const canRender = await resolveCustomDomain();
  if (!canRender && isCustomDomain()) {
    // Unknown/inactive custom domain — show a neutral message so we don't
    // leak any org data by falling through to the normal app shell.
    document.getElementById('root')!.innerHTML =
      '<div style="display:flex;height:100vh;flex-direction:column;gap:12px;align-items:center;justify-content:center;font-family:system-ui">' +
      '<div>This domain is not configured. Please contact your administrator.</div>' +
      '<button style="padding:6px 16px;border:1px solid #ccc;border-radius:6px;background:none;cursor:pointer;font:inherit" ' +
      'onclick="window.location.reload()">Retry</button></div>';
  } else {
    createRoot(document.getElementById('root')!).render(
      // <StrictMode>
      <Providers />,
      // </StrictMode>
    );
  }

  // Service worker is WEB-PWA-ONLY. The Tauri desktop app serves its shell
  // natively and uses the `offlineres://` custom protocol for offline resources —
  // it doesn't need a SW, and service workers are unreliable in Tauri's WKWebView
  // (a flaky SW can wedge the app-shell load on relaunch). So never register it
  // inside Tauri.
  if (isTauri()) {
    // Clean up any service worker registered by an older build — Tauri must not
    // use one, and a stale SW can keep wedging the app-shell load on relaunch.
    navigator.serviceWorker?.getRegistrations?.().then(
      (regs) => regs.forEach((r) => void r.unregister()),
      () => {},
    );
    // Tell Rust the resources origin so the offlineres:// proxy can resolve cache
    // misses (fetch the real CloudFront URL through the shared cookie jar).
    void import('@tauri-apps/api/core').then(({ invoke }) =>
      invoke('set_resources_base', { base: import.meta.env.VITE_RESOURCES_ENDPOINT }).catch(() => {}),
    );
  } else if ('serviceWorker' in navigator) {
    registerSW({ immediate: true });
    // The SW skipWaiting()s and clients.claim()s, so a deploy's new worker
    // takes control of this tab mid-session — but the tab keeps running the
    // OLD bundle until it reloads, which historically meant users needed a
    // second visit (and got stale behavior, e.g. an outdated login flow on
    // custom domains). Reload once when a new worker takes over, unless the
    // user is inside the 3D viewer (never yank a modeling session; the next
    // natural navigation picks the update up there).
    let swReloaded = false;
    // clients.claim() also fires controllerchange on a first-time visitor's
    // initial install — only reload when a controller is being REPLACED.
    let hadController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController) {
        hadController = true;
        return;
      }
      if (swReloaded) return;
      if (window.location.pathname.includes('/projects/')) return;
      swReloaded = true;
      window.location.reload();
    });
  }
  // Ask the OS not to evict offline data under disk pressure (web/OPFS only;
  // native FS has no quota).
  if (!isTauri() && navigator.storage?.persist) {
    void navigator.storage.persist();
  }
})();
